import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { LoadingController, ModalController, NavController } from '@ionic/angular';
import { from, Observable, switchMap } from 'rxjs';

import { AbstractRequestFormAdaptor } from '../../common/form/abstract-request-form.adaptor';
import { normalizePhoneAU } from '../../common/validators/phone-au.validator';
import { ReferralUploadModalComponent } from './referral-upload-modal.component';
import { AnalyticsService } from '../analytics/analytics.service';

export interface ReferralFormData {
  survivor: {
    first_name: string;
    last_name: string;
    group: {
      email: string;
      phone: string;
    };
    date_of_birth: string;
    gender: string;
    address: string;
    can_make_decision: string;
    can_make_decision_data: string;
    communication_support: string[];
    interpreter_data: string;
    trained_person_data: string;
  };
  clinician: {
    title: string;
    first_name: string;
    last_name: string;
    health_professional_profession: string;
    email: string;
    phone: string;
    practice_name: string;
    practice_address: string;
  };
  carer: {
    first_name: string;
    last_name: string;
    email: string | null;
    phone: string;
    relationship: string;
  };
  stroke_information: {
    stroke_date: string;
    stroke_type: string;
    summary: string;
    hospital_name: string;
    referral_reason: string;
    document: File | null;
  };
}
interface ReferralFormResponse {}

export interface UploadReferralFormData {
  document: File | null;
}

interface UploadReferralResponse {}

@Injectable()
export class ReferralFormAdaptor extends AbstractRequestFormAdaptor<ReferralFormData, ReferralFormResponse> {
  name = 'referral-form';

  private http = inject(HttpClient);
  private navCtrl = inject(NavController);
  private modalCtrl = inject(ModalController);
  private analytics = inject(AnalyticsService);

  onRequest({
    survivor: { group: survivor_group, ...survivor_rest },
    carer,
    stroke_information,
    clinician,
  }: ReferralFormData): Observable<ReferralFormResponse> {
    const survivor = { ...survivor_group, ...survivor_rest };

    survivor.phone = survivor.phone?.startsWith('+')
      ? survivor.phone.replace(/[^\d+]/g, '')
      : normalizePhoneAU(survivor.phone);
    carer.phone = carer.phone?.startsWith('+') ? carer.phone.replace(/[^\d+]/g, '') : normalizePhoneAU(carer.phone);
    clinician.phone = clinician.phone?.startsWith('+')
      ? clinician.phone.replace(/[^\d+]/g, '')
      : normalizePhoneAU(clinician.phone);

    const form = new FormData();

    append('survivor', survivor);
    append('carer', carer);
    append('clinician', clinician);
    append('stroke_information', stroke_information);

    return this.http.post('/referrals', form);

    function append(name: string, obj: Record<string, string | File | null | any[]>) {
      for (const [key, value] of Object.entries(obj)) {
        if (value == null) continue;

        if (Array.isArray(value)) {
          // Handle communication_support as JSON string for backend
          form.append(`${name}[${key}]`, JSON.stringify(value));
        } else {
          // Otherwise, append the single value
          form.append(`${name}[${key}]`, value);
        }
      }
    }
  }

  override onSuccess() {
    // Track successful form submission
    this.analytics.trackFormSubmitted('online_referral_form', true);

    this.navCtrl.navigateForward(['referral-form', 'sent'], { replaceUrl: true });
  }

  override async onError(_formData: ReferralFormData, errorRes: unknown, form: FormGroup): Promise<void> {
    let msg = 'We are sorry. There was an error.<br>Your referral was not submitted.<br>Please try again later, or contact the YSS team for help.';
    let title = 'Submission Failed';
    let errorMessage = 'Unknown error';

    if (errorRes instanceof HttpErrorResponse) {
      if (errorRes.status === 409) {
        msg = 'The survivor profile already exists.';
        errorMessage = `HTTP ${errorRes.status}: Survivor profile already exists`;
      } else {
        errorMessage = `HTTP ${errorRes.status}: ${errorRes.message}`;
      }
    }

    // Track failed form submission
    this.analytics.trackFormSubmitted('online_referral_form', false, errorMessage);

    form.setErrors(null);
    const modal = await this.modalCtrl.create({
      component: ReferralUploadModalComponent,
      cssClass: 'custom-error-modal modal-box custom-modal',
      componentProps: { title, message: msg },
    });
    await modal.present();
  }
}

@Injectable()
export class UploadReferralFormAdaptor extends AbstractRequestFormAdaptor<
  UploadReferralFormData,
  UploadReferralResponse
> {
  override name = 'upload-referral';

  private http = inject(HttpClient);
  private modalCtrl = inject(ModalController);
  private loadingCtrl = inject(LoadingController);
  private loading: HTMLIonLoadingElement | null = null;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  override onRequest(formData: UploadReferralFormData) {
    const form2 = new FormData();
    form2.append(`referral_letters[letter]`, Object.values(formData)[0]);
    // Create and present the loading overlay before making the HTTP request so that
    // this.loading is guaranteed to exist when onSuccess/onError run.
    return from(this.loadingCtrl.create({ message: 'Uploading referral form, please wait ...' })).pipe(
      switchMap(loading => {
        this.loading = loading;
        return from(loading.present());
      }),
      switchMap(() => this.http.post<UploadReferralFormData>('/referral_letters', form2))
    );
  }

  override async onSuccess(_: UploadReferralFormData, res: any) {
    await this.loading?.dismiss();
    this.loading = null;
    const modal = await this.modalCtrl.create({
      component: ReferralUploadModalComponent,
      cssClass: 'custom-success-modal modal-box custom-modal',
      componentProps: {
        title: res.title ? res.title : 'Referral Form Uploaded Successfully',
        message: res.message
          ? res.message
          : 'Your referral form has been uploaded successfully. Our team will contact you shortly.',
      },
    });
    await modal.present();
  }

  override async onError(_formData: UploadReferralFormData, errorRes: unknown) {
    /* istanbul ignore next */
    await this.loading?.dismiss();
    this.loading = null;
    let error;
    if (errorRes instanceof HttpErrorResponse) {
      error = errorRes.error;
    }
    const modal = await this.modalCtrl.create({
      component: ReferralUploadModalComponent,
      cssClass: 'custom-error-modal modal-box custom-modal',
      componentProps: {
        title: error?.title ? error.title : 'Upload Failed',
        message: error?.message
          ? error.message
          : 'There was an error uploading the referral form. Please try again later.',
      },
    });
    await modal.present();
  }
}
